import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {ROOT_URL} from "../../../config/url";
import {
  getErrorMessageByKey,
  Input,
  isValidateNullText,
  jsonFormatText,
  Modal,
  Select,
  useList,
  useLocalStorage,
  useNotification,
  useObject,
} from "@vokymlak/kabinet-ui";
import moment from "moment";
import WeightModal from "./WeightModal";
import StickerLabel from "./StickerLabel";

function ModalStickerPrint(props) {
  const [weightData, setWeightData] = useState({value: null, stable: false});
  const [isUpdateSampleData, setIsUpdateSampleData] = useState(false);
  const [isRecordSizeFromScale, setIsRecordSizeFromScale] = useState(false);
  const [isPrintAuto, setIsPrintAuto] = useState(false);

  const [manualValue, setManualValue] = useState(0);

  // Состояния для автоматической печати
  const [autoPrintStatus, setAutoPrintStatus] = useState('idle');
  const [countdown, setCountdown] = useState(0);
  const [lastPrintedWeight, setLastPrintedWeight] = useState(null);

  // ----------------------------------------------------------------------------------------------------
  const [localSettings, setLocalSettings] = useLocalStorage(
    "printer",
    {
      printerUuid: null,
      isFullQR: false,
      // По умолчанию false - БЕЗ даты производства
      withoutManufactureDate: false,
    }
  );
  const [printerUuid, setPrinterUuid] = useState(
    localSettings.printerUuid || null
  );
  const [isFullQR, setIsFullQR] = useState(localSettings.isFullQR);
  // По умолчанию false - показываем дату производства
  const [withoutManufactureDate, setWithoutManufactureDate] = useState(localSettings.withoutManufactureDate);

  useEffect(() => {
    setLocalSettings({
      printerUuid,
      // isFullQR: isFullQR,
      isFullQR: false,
      withoutManufactureDate: withoutManufactureDate,
    });
  }, [
    printerUuid,
    isFullQR,
    withoutManufactureDate,
  ]);
  // ----------------------------------------------------------------------------------------------------

  const inputPrinter = useRef(null);
  const inputNumberCopies = useRef(null);
  const inputManufactureDate = useRef(null);

  const {notification} = useNotification();

  const {
    list: [printers],
  } = useList({
    apiUrl: "/api/printers/list",
    rootUrl: ROOT_URL,
    callbackResponse: (response) => {
      if (!printerUuid) {
        setPrinterUuid(response?.length ? response[0].uuid : null);
      }
    },
  });

  // Добавляем состояние для начальной даты
  const [initialDate] = useState(moment().format("YYYY-MM-DD HH:mm"));

  const {
    object: [nomenclature],
  } = useObject({
    apiUrl: "/api/nomenclature/" + props.object.uuid,
    rootUrl: ROOT_URL,
    callbackResponse: (response) => {
      setManualValue(!response.is_pcs ?
        (Number(((!!response?.value && !!response?.measurement_unit_type ?
          (response.value / response.measurement_unit_type.ratio) :
          !!response?.value && response.value)).toFixed(3))) || null
        : 1
      );
    },
    callbackError: () => {
      props.handleClose();
    },
  });

  // Обработчик изменений весов
  useEffect(() => {
    if (!isPrintAuto || !isRecordSizeFromScale) return;

    handleWeightChange();
  }, [weightData, isPrintAuto]);

  const handleWeightChange = () => {
    if (!isPrintAuto) return;

    switch (autoPrintStatus) {
      case 'waiting_zero':
        // Ждем когда вес будет 0 И весы будут в покое
        if ((weightData.value === 0 || weightData.value === null) && weightData.stable) {
          setAutoPrintStatus('waiting_product');
          notification("Весы обнулены и в покое. Положите продукт на весы", "blue");
        }
        break;

      case 'waiting_product':
        // Ждем положительный вес И стабильное состояние
        if (weightData.value > 0 && weightData.stable) {
          startPrintCountdown();
        }
        break;

      case 'counting':
        // Если вес изменился ИЛИ весы не в покое - прерываем
        if (weightData.value !== lastPrintedWeight || !weightData.stable) {
          setAutoPrintStatus('waiting_zero');
          setCountdown(0);
          notification("Вес изменился или весы не в покое. Начинаем заново...", "blue");
        }
        break;

      case 'success':
        // После успешной печати ждем обнуления весов И состояния покоя
        if ((weightData.value === 0 || weightData.value === null) && weightData.stable) {
          setAutoPrintStatus('waiting_product');
          notification("Готов к следующему продукту", "blue");
        }
        break;
    }
  };

  const startPrintCountdown = () => {
    setAutoPrintStatus('counting');
    setLastPrintedWeight(weightData.value);
    setCountdown(0); // Устанавливаем 0 вместо 3

    // ЗАКОММЕНТИРОВАН ОТСЧЕТ 3-2-1
    /*
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          executeAutoPrint();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    */

    // Немедленный запуск печати
    executeAutoPrint();
  };

  const executeAutoPrint = () => {
    setAutoPrintStatus('printing');

    let manufactureDate = null;
    // Если НЕ стоит "Без даты производства", то проверяем дату
    if (!withoutManufactureDate) {
      manufactureDate = jsonFormatText(inputManufactureDate.current.value);

      // Проверяем дату только если она должна быть указана
      if (isValidateNullText(manufactureDate)) {
        setAutoPrintStatus('error');
        notification("Ошибка: не заполнена дата производства", "red");
        return;
      }
    }

    const v = Number(Number(weightData.value).toFixed(3));

    const jsonData = JSON.stringify({
      is_full_qr: isFullQR,
      // Если "Без даты производства" - отправляем null, иначе дату
      manufacture_date: (!withoutManufactureDate && !!manufactureDate) ? manufactureDate + ":00Z" : null,
      value: v,
      number_copies: 1,
      printer: !!printerUuid && printerUuid.length > 0 && printerUuid !== 'null' ? {
        uuid: printerUuid,
      } : null,
    });

    axios
      .post("/api/nomenclature/" + props.object.uuid + "/sticker/print", jsonData, {
        withCredentials: false,
        baseURL: ROOT_URL,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("tokenSession"),
        },
      })
      .then(() => {
        setAutoPrintStatus('success');
        notification("Этикетка распечатана!", "green");
        props.successfulRequest();

        // Уменьшен таймаут с 2000 до 500 мс
        setTimeout(() => {
          setAutoPrintStatus('waiting_zero');
          notification("Снимите продукт с весов для продолжения", "blue");
        }, 500);
      })
      .catch((error) => {
        setAutoPrintStatus('error');
        notification(getErrorMessageByKey(error.response.data.error), "red");

        // Уменьшен таймаут с 3000 до 300 мс
        setTimeout(() => {
          setAutoPrintStatus('waiting_zero');
        }, 300);
      });
  };

  const startAutoPrint = () => {
    if (!printerUuid || printerUuid === 'null') {
      notification("Выберите принтер для автоматической печати", "red");
      return;
    }

    setIsPrintAuto(true);

    // Проверяем текущее состояние весов - должен быть 0 И в покое
    if (weightData.value > 0 || !weightData.stable) {
      setAutoPrintStatus('waiting_zero');
      notification("Снимите продукт с весов и дождитесь состояния покоя для начала автоматической печати", "red");
    } else {
      setAutoPrintStatus('waiting_product');
      notification("Автопечать запущена. Положите продукт на весы", "blue");
    }
  };

  const stopAutoPrint = () => {
    setIsPrintAuto(false);
    setAutoPrintStatus('idle');
    setCountdown(0);
    notification("Автоматическая печать остановлена", "green");
  };

  const changeDownloadPDF = () => {
    let manufactureDate = null;
    // Если НЕ стоит "Без даты производства", то проверяем дату
    if (!withoutManufactureDate) {
      manufactureDate = jsonFormatText(inputManufactureDate.current.value);

      if (isValidateNullText(manufactureDate)) {
        notification("Заполните все обязательные поля", "red");
        return;
      }
    }

    let value, v;

    if (isRecordSizeFromScale) {
      value = weightData.value
    } else {
      value = manualValue
    }

    if (nomenclature.is_pcs) {
      v = Number(value);
    } else {
      v = Number(Number(value).toFixed(3));
    }

    const jsonData = JSON.stringify({
      is_full_qr: isFullQR,
      manufacture_date: (!withoutManufactureDate && !!manufactureDate) ? manufactureDate + ":00Z" : null,
      value: v,
    });

    axios
      .post("/api/nomenclature/" + props.object.uuid + "/export/print/sticker/pdf",
        jsonData,
        {
          responseType: "arraybuffer",
          withCredentials: false,
          baseURL: ROOT_URL,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/pdf",
            Authorization: "Bearer " + localStorage.getItem("tokenSession"),
          },
        })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "№" + props.object.id + (!!props.object.name && '_' + props.object.name) + ".pdf"
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        notification(getErrorMessageByKey(error.response.data.error), "red");
      });
  };

  const changePrint = () => {
    const numberCopies = jsonFormatText(inputNumberCopies.current.value);
    const printerUuid = jsonFormatText(inputPrinter.current.value);
    let manufactureDate = null;

    // Общие проверки (независимо от даты)
    if (isValidateNullText(numberCopies) || Number(numberCopies) < 1) {
      notification("Заполните все обязательные поля", "red");
      return;
    }

    if (isValidateNullText(printerUuid) || printerUuid === 'null') {
      notification("Заполните все обязательные поля", "red");
      return;
    }

    // Проверяем дату только если НЕ стоит "Без даты производства"
    if (!withoutManufactureDate) {
      manufactureDate = jsonFormatText(inputManufactureDate.current.value);

      if (isValidateNullText(manufactureDate)) {
        notification("Заполните все обязательные поля", "red");
        return;
      }
    }

    let value, v;

    if (isRecordSizeFromScale) {
      value = weightData.value
    } else {
      value = manualValue
    }

    if (nomenclature.is_pcs) {
      v = Number(value);
    } else {
      v = Number(Number(value).toFixed(3));
    }

    const jsonData = JSON.stringify({
      is_full_qr: isFullQR,
      manufacture_date: (!withoutManufactureDate && !!manufactureDate) ? manufactureDate + ":00Z" : null,
      value: v,
      number_copies: Number(numberCopies),
      printer: !!printerUuid && printerUuid.length > 0 && printerUuid !== 'null' ? {
        uuid: printerUuid,
      } : null,
    });

    axios
      .post("/api/nomenclature/" + props.object.uuid + "/sticker/print", jsonData, {
        withCredentials: false,
        baseURL: ROOT_URL,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("tokenSession"),
        },
      })
      .then(() => {
        props.successfulRequest();
        notification("Отправлено на печать", "blue");
        // props.handleClose();
      })
      .catch((error) => {
        notification(getErrorMessageByKey(error.response.data.error), "red");
      });
  };

  const getPrinterSelectOptions = (list) => {
    const options = [];

    list.map((item) => {
      const option = {
        value: item.uuid,
        title: item.name,
      };

      options.push(option);
    });

    return options;
  };

  // Добавляем состояние для даты производства
  const [manufactureDateValue, setManufactureDateValue] = useState(initialDate);

  // Обновляем manufactureDateValue при изменении withoutManufactureDate или inputManufactureDate
  useEffect(() => {
    if (!withoutManufactureDate) {
      // Используем значение из ref или начальную дату
      const dateValue = inputManufactureDate.current?.value || initialDate;
      setManufactureDateValue(dateValue);
    } else {
      setManufactureDateValue(null);
    }
  }, [withoutManufactureDate, isUpdateSampleData, initialDate]);

  // Инициализируем manufactureDateValue при загрузке номенклатуры
  useEffect(() => {
    if (nomenclature && !withoutManufactureDate) {
      setManufactureDateValue(initialDate);
    }
  }, [nomenclature, withoutManufactureDate, initialDate]);

  const sampleData = useMemo(() => ({
    item: {
      // Используем manufactureDateValue вместо вызова функции
      manufactureDate: !withoutManufactureDate ? manufactureDateValue : null,
      value: isRecordSizeFromScale ? (weightData.value ? weightData.value : 0) : manualValue
    },
    object: {
      id: nomenclature?.id,
      name: nomenclature?.name,
      structure: nomenclature?.structure,
      article: nomenclature?.article,
      isPcs: nomenclature?.is_pcs,
      expirationDays: nomenclature?.expiration_days,
      measurementUnitType: {
        designation: nomenclature?.measurement_unit_type.designation,
        ratio: nomenclature?.measurement_unit_type.ratio,
      }
    },
    branch: {
      shortName: null
    }
  }), [nomenclature, manualValue, manufactureDateValue, isRecordSizeFromScale, weightData, withoutManufactureDate]);

  const renderAutoPrintStatus = () => {
    const statusConfig = {
      idle: {message: "Готов к работе", color: "#666"},
      waiting_zero: {message: "Снимите продукт с весов", color: "#ff9800"},
      waiting_product: {message: "Положите продукт на весы", color: "#2196f3"},
      counting: {message: `Печать через: ${countdown}`, color: "#ff5722"},
      printing: {message: "Идет печать...", color: "#9c27b0"},
      success: {message: "Печать успешна!", color: "#4caf50"},
      error: {message: "Ошибка печати", color: "#f44336"},
    };

    const config = statusConfig[autoPrintStatus] || statusConfig.idle;

    return (
      <div style={{
        padding: "12px",
        backgroundColor: config.color + "20",
        border: `2px solid ${config.color}`,
        borderRadius: "6px",
        textAlign: "center",
        marginBottom: "16px",
        fontWeight: "bold",
        color: config.color
      }}>
        {config.message}
      </div>
    );
  };

  // Функция для обработки изменения даты
  const handleManufactureDateChange = () => {
    setIsUpdateSampleData(!isUpdateSampleData);
    // Обновляем manufactureDateValue при изменении поля ввода
    if (inputManufactureDate.current && inputManufactureDate.current.value) {
      setManufactureDateValue(inputManufactureDate.current.value);
    }
  };

  return (
    !!nomenclature && (
      <>
        <Modal handleClose={() => null}>
          <div className="Form">
            <div className="Form__title">
              {nomenclature.name}
              <div className="Text__signature">
                Печать этикетки
              </div>
            </div>

            {/* Поле ввода даты производства - отображается только если НЕ выбран "Без даты производства" */}
            {!withoutManufactureDate && (
              <div className="Form__field">
                <Input
                  type={'datetime-local'}
                  label="Дата производства *"
                  value={initialDate}
                  reference={inputManufactureDate}
                  onChange={handleManufactureDateChange}
                />
              </div>
            )}

            {/* Тумблер для включения/отключения даты производства */}
            <div className="Form__field">
              <div
                className="Button__swith"
                style={{
                  padding: "8px",
                  width: "100%",
                  border: withoutManufactureDate
                    ? "2px solid #ffe082"
                    : "2px dashed #20202015",
                  backgroundColor: withoutManufactureDate ? "#ffe082" : "#ffffff",
                  borderRadius: 6,
                  marginTop: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setWithoutManufactureDate(!withoutManufactureDate);
                  setIsUpdateSampleData(!isUpdateSampleData);
                }}
              >
                <div
                  className="Swith__text"
                  style={{
                    fontSize: 17,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Без даты производства
                </div>
                <div
                  className={
                    "Swith Swith_gray" +
                    ((withoutManufactureDate && " Swith_active Swith_active_gray") || "")
                  }
                ></div>
              </div>
            </div>

            {nomenclature.is_pcs && (
              <div className="Form__field">
                <Input type={'number'} label={"Количество в упаковке *"}
                       value={manualValue}
                       onChange={(e) => {
                         setManualValue(e.target.value);
                         setIsUpdateSampleData(!isUpdateSampleData);
                       }}
                />
              </div>
            )}

            {!nomenclature.is_pcs && !isRecordSizeFromScale && (
              <div className="Form__field">
                <Input type={'number'} label={"Размер (" +
                  (!!nomenclature.measurement_unit_type ? nomenclature.measurement_unit_type.designation : "-") + ") *"}
                       value={manualValue}
                       onChange={(e) => {
                         setManualValue(e.target.value);
                         setIsUpdateSampleData(!isUpdateSampleData);
                       }}
                />
              </div>
            )}

            {!nomenclature.is_pcs && (
              <div className="Form__field">
                <div
                  className="Button__swith"
                  style={{
                    padding: "8px",
                    width: "100%",
                    border: isRecordSizeFromScale
                      ? "2px solid #ffe082"
                      : "2px dashed #20202015",
                    backgroundColor: isRecordSizeFromScale ? "#ffe082" : "#ffffff",
                    borderRadius: 6,
                    marginTop: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => setIsRecordSizeFromScale(!isRecordSizeFromScale)}
                >
                  <div
                    className="Swith__text"
                    style={{
                      fontSize: 17,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Фиксировать размер с весов
                  </div>
                  <div
                    className={
                      "Swith Swith_gray" +
                      ((isRecordSizeFromScale && " Swith_active Swith_active_gray") || "")
                    }
                  ></div>
                </div>
              </div>
            )}

            {!nomenclature.is_pcs && isRecordSizeFromScale && (
              <div className="Form__field">
                <WeightModal setWeightData={setWeightData}/>
              </div>
            )}

            {isPrintAuto && (
              <div className="Form__field">
                <div style={{
                  position: "relative",
                  width: 292,
                  height: 220 / 4 * 3,
                }}>
                  <div className="Field__label">Ход печати</div>
                  {/*<div className="Field__label">Пример печати</div>*/}
                  {/*<StickerLabel*/}
                  {/*  print={sampleData.print}*/}
                  {/*  item={sampleData.item}*/}
                  {/*  object={sampleData.object}*/}
                  {/*  branch={sampleData.branch}*/}
                  {/*  code="sample-code"*/}
                  {/*/>*/}

                  {isPrintAuto && (
                    <div style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 0,
                      // backgroundColor: "rgba(255,255,255,0.85)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "4px"
                    }}>
                      {renderAutoPrintStatus()}

                      {/* ЗАКОММЕНТИРОВАН ОТСЧЕТ 3-2-1
                    {autoPrintStatus === 'counting' && (
                      <div style={{
                        fontSize: "48px",
                        fontWeight: "bold",
                        color: "#ff5722"
                      }}>
                        {countdown}
                      </div>
                    )}
                    */}

                      {autoPrintStatus === 'printing' && (
                        <div style={{
                          fontSize: "24px",
                          color: "#9c27b0"
                        }}>
                          ⏳ Печатается...
                        </div>
                      )}

                      {autoPrintStatus === 'success' && (
                        <div style={{
                          fontSize: "24px",
                          color: "#4caf50"
                        }}>
                          ✅ Успешно!
                        </div>
                      )}

                      {autoPrintStatus === 'error' && (
                        <div style={{
                          fontSize: "24px",
                          color: "#f44336"
                        }}>
                          ❌ Ошибка
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/*{!isPrintAuto && (*/}
            {/*  <div className="Form__field">*/}
            {/*    <div*/}
            {/*      className="Button__swith"*/}
            {/*      style={{*/}
            {/*        padding: "8px",*/}
            {/*        width: "100%",*/}
            {/*        border: isFullQR*/}
            {/*          ? "2px solid #ffe082"*/}
            {/*          : "2px dashed #20202015",*/}
            {/*        backgroundColor: isFullQR ? "#ffe082" : "#ffffff",*/}
            {/*        borderRadius: 6,*/}
            {/*        marginTop: 20,*/}
            {/*        display: "flex",*/}
            {/*        alignItems: "center",*/}
            {/*        justifyContent: "space-between",*/}
            {/*        cursor: "pointer",*/}
            {/*      }}*/}
            {/*      onClick={() => setIsFullQR(!isFullQR)}*/}
            {/*    >*/}
            {/*      <div*/}
            {/*        className="Swith__text"*/}
            {/*        style={{*/}
            {/*          fontSize: 17,*/}
            {/*          fontWeight: 600,*/}
            {/*          display: "flex",*/}
            {/*          alignItems: "center",*/}
            {/*        }}*/}
            {/*      >*/}
            {/*        Полный QR (данные о товаре)*/}
            {/*      </div>*/}
            {/*      <div*/}
            {/*        className={*/}
            {/*          "Swith Swith_gray" +*/}
            {/*          ((isFullQR && " Swith_active Swith_active_gray") || "")*/}
            {/*        }*/}
            {/*      ></div>*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*)}*/}

            {/*{isPrintAuto && (*/}
            {/*  <div className="Form__field">*/}
            {/*    <div style={{ fontSize: "14px", marginTop: "8px", color: "#666" }}>*/}
            {/*      Метка: <strong>{"ШТРИХ-код"}</strong> и <strong>{"QR-код"}</strong> {(isFullQR && "+ данные о товаре") || ''}*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*)}*/}

            <div className="Form__field Form__field_last" style={{
              border: "2px solid #0061fc90",
              borderRadius: 4,
              padding: 12,
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
            }}>
              <div className="Form__field">
                <Select
                  isFixed={true}
                  options={getPrinterSelectOptions(printers)}
                  label="Принтер"
                  reference={inputPrinter}
                  onChange={(event) => (setPrinterUuid(event.target.value), console.log(event.target.value))}
                  value={printerUuid}
                />
              </div>

              {!isPrintAuto && (
                <div className="Form__field Form__field_last">
                  <Input
                    reference={inputNumberCopies}
                    type={'number'}
                    label="Количество этикеток *"
                    value={1}
                  />
                </div>
              )}
            </div>

            {!nomenclature.is_pcs && isRecordSizeFromScale && (
              <div className="Form__actions Form__actions_center" style={{
                marginBottom: 12
              }}>
                <button
                  className={`Action__button Action__button_all ${!isPrintAuto ? 'Action__button_blue' : 'Action__button_red'}`}
                  onClick={!isPrintAuto ? startAutoPrint : stopAutoPrint}>
                  {!isPrintAuto && "Начать авто печать"}
                  {isPrintAuto && "Остановить авто печать"}
                </button>
              </div>
            )}

            {!isPrintAuto && (
              <div className="Form__actions">
                <div className="Action__link Action__link_blue"
                     onClick={(event) => changeDownloadPDF()}
                     style={{
                       textAlign: "center"
                     }}>
                  Скачать PDF <br/><span style={{
                  color: "#292929",
                  fontSize: 12
                }}>(для проверки с полным QR)</span>
                </div>
                <button className="Action__button Action__button_blue"
                        onClick={(event) => changePrint()}
                        style={{
                          paddingLeft: 32,
                          paddingRight: 32
                        }}>
                  Печать
                </button>
              </div>
            )}

            <div className="Form__actions Form__actions_center">
              <div className="Action__link" onClick={() => props.handleClose()}>
                Закрыть окно печати
              </div>
            </div>
          </div>
        </Modal>
      </>
    )
  );
}

export default ModalStickerPrint;
